import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useMarkAsReadMutation,
  useRemoveNotificationMutation,
} from "../../store/api/notificationApiSlice";
import {
  closeNotificationModal,
  patchActiveNotification,
  removeNotification as removeLiveNotification,
} from "../../store/slices/appSlice";
import userTypes from "../../constants/userTypes";
import { getNotificationInboxId } from "../../utils/notificationHelpers";
import NotificationDetailModal from "./NotificationDetailModal";

const GlobalNotificationModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeNotification = useSelector((state) => state.app.activeNotification);
  const userTypeUpper = String(
    useSelector((state) => state.auth?.user?.userType || state.auth?.role?.name || "")
  ).toUpperCase();
  const isDoctor = userTypeUpper === userTypes.DOCTOR;

  const [markAsRead] = useMarkAsReadMutation();
  const [removeNotification] = useRemoveNotificationMutation();
  const [markingId, setMarkingId] = useState(null);

  const markNotificationAsRead = useCallback(
    async (id, { silent = false } = {}) => {
      const inboxId = getNotificationInboxId({ id, data: activeNotification?.data }) ?? id;
      try {
        setMarkingId(inboxId);
        const res = await markAsRead(inboxId).unwrap();
        if (res?.isSuccess !== false) {
          if (!silent) toast.success(t("marked_as_read"));
          dispatch(patchActiveNotification({ isRead: true }));
          return true;
        }
        if (!silent) toast.error(t("error_messages.something_went_wrong"));
        return false;
      } catch {
        if (!silent) toast.error(t("error_messages.something_went_wrong"));
        return false;
      } finally {
        setMarkingId(null);
      }
    },
    [activeNotification?.data, dispatch, markAsRead, t]
  );

  if (!isDoctor) return null;

  const handleClose = () => {
    dispatch(closeNotificationModal());
  };

  const handleDelete = async (id) => {
    const inboxId = getNotificationInboxId(activeNotification) ?? id;
    try {
      const res = await removeNotification(inboxId).unwrap();
      if (res?.isSuccess !== false) {
        toast.success(t("removed_successfully"));
        dispatch(removeLiveNotification(id));
        dispatch(closeNotificationModal());
      } else {
        toast.error(t("error_messages.something_went_wrong"));
      }
    } catch {
      toast.error(t("error_messages.something_went_wrong"));
    }
  };

  const handleNavigate = (path) => {
    dispatch(closeNotificationModal());
    navigate(path);
  };

  return (
    <NotificationDetailModal
      open={Boolean(activeNotification)}
      notification={activeNotification}
      markingId={markingId}
      onClose={handleClose}
      onMarkAsRead={(id) => markNotificationAsRead(id, { silent: false })}
      onDelete={handleDelete}
      onNavigate={handleNavigate}
    />
  );
};

export default GlobalNotificationModal;
